import * as React from 'react';
import { connect } from 'react-redux';

import PromptExit from 'libs/router/prompt-exit/PromptExit';

import { ModalFrame, ModalFrameFooter, ModalFrameInner } from 'components/modal';
import ActionButton from 'components/buttons/action-button/ActionButton';
import { Form } from 'components/form';
import CampaignPayoutsVariablePaymentsBudget from './CampaignPayoutsVariablePaymentsBudget';
import CampaignPayoutsVariablePaymentsModalBody from './body/CampaignPayoutsVariablePaymentsModalBody';
import SubcampaignsMultiSelect from 'modules/subcampaigns/components/bulk/subcampaigns-multi-select/SubcampaignsMultiSelect';

import { __t, applyTranslations } from 'utils/language';
import { getVariablePaymentsById, isVariablePaymentNew, populateEmptyFieldsForVariablePayment } from 'utils/payouts';
import { isOther } from 'utils/subcampaign';

import type { PaymentCampaignBudget, Subcampaign, SubcampaignSelectedIdsMap, TextEntry, VariablePayment } from 'types';

import './bulk-variable-payments-modal.scss';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./bulk-variable-payments-modal.i18n.json'));

type OwnProps = {
  campaignName: string,
  isLoading: boolean,
  campaignVariablePayments: Array<VariablePayment>,
  campaignBudgetInfo: PaymentCampaignBudget,
  subcampaignsList: Array<Subcampaign>,
  onBulkEditVariablePayments: (a: Array<VariablePayment>) => void,
  onCloseModal: () => void
};

type StateProps = {
  mapSubcampaignToInfluencerIds: {
    [x: number]: number
  }
};

type Props = OwnProps & StateProps;

type State = {
  stepIndex: number,
  isModified: boolean,
  remainingBudget: number,
  selectedIdsMap: SubcampaignSelectedIdsMap,
  variablePaymentsBySubcampaignId: {
    [k in number | string]: Array<VariablePayment>;
  }
};

const STEPS_COUNT = 2;
const SUBCAMPAIGNS_SCROLLABLE_HEIGHT = 4;
const ITEMS_CONTAINER_HEIGHT = 355;

class BulkVariablePaymentsModal extends React.Component<Props, State> {
  itemsContainer: HTMLElement | undefined | null;
  form = React.createRef<Form>();

  constructor(props: Props) {
    super(props);

    this.state = {
      stepIndex: 0,
      isModified: false,
      remainingBudget: this.getDefaultRemainingBudget(),
      selectedIdsMap: this.getDefaultSubcampaignIdsList(),
      variablePaymentsBySubcampaignId: this.props.subcampaignsList.reduce((res, subcampaign) => {
        res[subcampaign.id] = this.getVariablePaymentsBySubcampaign(subcampaign.id);
        return res;
      }, {})
    };
  }

  getDefaultRemainingBudget = () => (this.props.campaignBudgetInfo || {}).remaining || 0;

  getDefaultSubcampaignIdsList = () => this.props.subcampaignsList.reduce((res: SubcampaignSelectedIdsMap, subcampaign: Subcampaign) => {
    res[subcampaign.id] = false;
    return res;
  }, {});

  getVariablePaymentsBySubcampaign(subcampaignId: number): Array<VariablePayment> {
    return getVariablePaymentsById('subcampaignId', subcampaignId, this.props.campaignVariablePayments) || [];
  }

  handleNext = () => {
    this.setState({
      stepIndex: 1
    });
  };

  handleSubmit = () => {
    const { onBulkEditVariablePayments, onCloseModal } = this.props;
    const { selectedIdsMap, variablePaymentsBySubcampaignId } = this.state;

    const updatedVariablePayments = Object.keys(selectedIdsMap)
      .filter(subcampaignId => selectedIdsMap[subcampaignId])
      .reduce<Array<VariablePayment>>((payments, subcampaignId) => [
        ...payments,
        ...(variablePaymentsBySubcampaignId[subcampaignId] || []).map(item => ({
          ...item,
          id: isVariablePaymentNew(item) ? null : item.id
        }))
      ], []);

    onBulkEditVariablePayments(updatedVariablePayments);

    onCloseModal();
  };

  handleSubmitForm = (event: SyntheticEvent<HTMLFormElement>) => {
    if (this.form && this.form.current) {
      this.form.current.handleSubmit(event);
    }
  };

  handleFieldAdd = (subcampaignId: number) => {
    const { mapSubcampaignToInfluencerIds } = this.props;
    const influencerProfileId = mapSubcampaignToInfluencerIds[subcampaignId];

    this.setState(state => ({
      variablePaymentsBySubcampaignId: {
        ...state.variablePaymentsBySubcampaignId,
        [subcampaignId]: [
          ...state.variablePaymentsBySubcampaignId[subcampaignId],
          { ...populateEmptyFieldsForVariablePayment(subcampaignId, influencerProfileId) }
        ]
      },
      isModified: true
    }));
  };

  handleFieldRemove = (id: number, subcampaignId: number): void => {
    this.setState(state => {
      // @ts-ignore - Cannot assign object literal to `removedItem` because inexact object literal is incompatible with exact `VariablePayment`
      let removedItem: VariablePayment = {};

      const updatedSubcampaign = state
        .variablePaymentsBySubcampaignId[subcampaignId]
        .map((item) => {
          if (item.id === id) {
            // saving for recalculating remaining budget
            removedItem = item;

            return {
              ...item,
              isDeleted: true
            };
          }

          return item;
        })
        .filter(item => !(isVariablePaymentNew(item) && item.isDeleted));

      return ({
        remainingBudget: state.remainingBudget + (removedItem.amount || 0),
        variablePaymentsBySubcampaignId: {
          ...state.variablePaymentsBySubcampaignId,
          [subcampaignId]: updatedSubcampaign
        },
        isModified: true
      });
    });
  };

  handleFieldChange = (event: SyntheticEvent<TextEntry>, subcampaignId: number) => {
    const target = event.currentTarget;
    const name = target.name || '';
    const value = target.value || '';
    const id = Number(target.dataset.id) || 0;

    // COLLAB-9276
    let amount: number | string = (name === 'amount' && Number(value)) || '';
    amount = amount === 0 ? '' : amount;

    this.setState(state => {
      let oldAmount = 0;

      const updatedSubcampaign = state.variablePaymentsBySubcampaignId[subcampaignId].map((item) => {
        if (item.id === id) {
          // saving for recalculating remaining budget
          oldAmount = item.amount;

          return {
            ...item,
            // $FlowIgnore - Flow is confused about allowing numbers and/or strings even though the types are correct
            [name]: name === 'amount' ? amount : value
          };
        }

        return item;
      });

      return ({
        remainingBudget: name === 'amount'
          ? state.remainingBudget + oldAmount - Number(value)
          : state.remainingBudget,
        variablePaymentsBySubcampaignId: {
          ...state.variablePaymentsBySubcampaignId,
          [subcampaignId]: updatedSubcampaign
        },
        isModified: true
      });
    });
  };

  isActionButtonDisabled() {
    const { selectedIdsMap, stepIndex } = this.state;

    if (stepIndex === STEPS_COUNT - 1) {
      return false;
    }

    return !Object.values(selectedIdsMap).some(Boolean);
  }

  handleToggle = (subcampaignId: number): void => {
    this.setState(state => ({
      ...state,
      selectedIdsMap: {
        ...state.selectedIdsMap,
        [subcampaignId]: !state.selectedIdsMap[subcampaignId]
      },
      isModified: true
    }));
  };

  handleToggleAll = (subcampaignIds: Array<number>, isSelected: boolean): void => {
    const selectedIdsMap = {};

    subcampaignIds.forEach(id => {
      selectedIdsMap[id] = isSelected;
    });

    this.setState(state => ({
      ...state,
      selectedIdsMap,
      isModified: true
    }));
  };

  shouldShowShadow() {
    const { stepIndex } = this.state;
    const { isLoading, subcampaignsList } = this.props;
    const isLastStep = stepIndex === STEPS_COUNT - 1;

    if (isLoading) {
      return false;
    }

    if (!isLastStep) {
      return subcampaignsList.length > SUBCAMPAIGNS_SCROLLABLE_HEIGHT;
    }

    return this.itemsContainer ? this.itemsContainer.scrollHeight > ITEMS_CONTAINER_HEIGHT : false;
  }

  renderButtons(): React.ReactNode {
    const { stepIndex } = this.state;
    const { onCloseModal } = this.props;
    const isLastStep = stepIndex === STEPS_COUNT - 1;

    return (
      <ModalFrameFooter>
        <ActionButton
          color="tertiary"
          onClick={onCloseModal}
        >
          {__t('collabary_common.action_button.cancel')}
        </ActionButton>
        <ActionButton
          disabled={isLastStep ? false : this.isActionButtonDisabled()}
          onClick={isLastStep ? this.handleSubmitForm : this.handleNext}
        >
          {__t(`collabary_common.action_button.${isLastStep ? 'save' : 'next'}`)}
        </ActionButton>
      </ModalFrameFooter>
    );
  }

  renderItem = (subcampaign: Subcampaign) => {
    const { isLoading } = this.props;
    const { variablePaymentsBySubcampaignId } = this.state;
    const subcampaignId = subcampaign.id;
    const otherArrangement = isOther(subcampaign);
    const agreedPayment = subcampaign.offer.budget || 0;

    return (
      <CampaignPayoutsVariablePaymentsModalBody
        key={`bulk-variable-payments-modal__item-${subcampaignId}`}
        className="bulk-variable-payments-modal__item"
        subcampaignId={subcampaignId}
        variablePayments={variablePaymentsBySubcampaignId[subcampaignId].filter(item => !item.isDeleted)}
        influencerProfile={subcampaign.creatorProfile}
        agreedPayment={agreedPayment}
        otherArrangement={otherArrangement}
        isLoading={isLoading}
        onFieldAdd={this.handleFieldAdd}
        onFieldRemove={this.handleFieldRemove}
        onFieldChange={this.handleFieldChange}
      />
    );
  };

  renderBudgetInfo(): React.ReactNode {
    const { campaignBudgetInfo } = this.props;
    const { remainingBudget } = this.state;
    const totalBudget = campaignBudgetInfo.total || 0;

    return (
      <CampaignPayoutsVariablePaymentsBudget
        totalBudget={totalBudget}
        remainingBudget={remainingBudget}
      />
    );
  }

  renderSteps() {
    const { selectedIdsMap, stepIndex } = this.state;
    const { isLoading, subcampaignsList } = this.props;
    const isLastStep = stepIndex === STEPS_COUNT - 1;

    if (isLastStep) {
      return (
        <Form
          ref={this.form}
          onSubmit={this.handleSubmit}
        >
          <div
            ref={(ref) => { this.itemsContainer = ref; }}
            className="bulk-variable-payments-modal__items"
          >
            {
              subcampaignsList
                .filter(subcampaign => selectedIdsMap[subcampaign.id])
                .map(this.renderItem)
            }
          </div>
        </Form>
      );
    }

    return (
      <SubcampaignsMultiSelect
        isLoading={isLoading}
        subcampaigns={subcampaignsList}
        selectedIdsMap={selectedIdsMap}
        titleAllWithSelectAll
        titleAll={__t('bulk_variable_payments_modal.subcampaigns_multi_select.column_title_to_select_from')}
        titleSelected={__t('bulk_variable_payments_modal.subcampaigns_multi_select.column_title_selected')}
        messageAllEmpty={__t('bulk_variable_payments_modal.subcampaigns_multi_select.message_empty_all')}
        messageSelectedEmpty={__t('bulk_variable_payments_modal.subcampaigns_multi_select.message_empty_selected')}
        onToggle={this.handleToggle}
        onToggleAll={this.handleToggleAll}
      />
    );
  }

  render(): React.ReactNode {
    const { campaignName } = this.props;
    const { stepIndex, isModified } = this.state;

    return (
      <ModalFrame
        title={__t('bulk_variable_payments_modal.title')}
        subtitle={__t('bulk_variable_payments_modal.subtitle', { campaignName })}
        data-qa="bulk-variable-payments-modal"
        stepCounter={{
          index: stepIndex + 1,
          count: STEPS_COUNT
        }}
      >
        {
          isModified &&
            <PromptExit type="modal" />
        }
        <ModalFrameInner>
          {this.renderBudgetInfo()}
          <div className="bulk-variable-payments-modal__step">
            {this.renderSteps()}
            {
              this.shouldShowShadow() && (
                <div className="bulk-variable-payments-modal__scroll-shadow" />
              )
            }
          </div>
        </ModalFrameInner>
        {this.renderButtons()}
      </ModalFrame>
    );
  }
}

const mapStateToProps = (state, props): StateProps => ({
  mapSubcampaignToInfluencerIds: props.subcampaignsList.reduce((res, subcampaign) => {
    res[subcampaign.id] = subcampaign.creatorProfile.id;
    return res;
  }, {})
});

export default connect(mapStateToProps)(BulkVariablePaymentsModal);
